import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import SidePanel from '@/components/shared/SidePanel'
import { SidePanelPayload, subscribeSidePanelOpen } from '@/lib/sidePanelEmitter'

type SidePanelContextValue = {
  open: () => void
  close: () => void
  isOpen: boolean
  closeCounter?: number
}

const SidePanelContext = createContext<SidePanelContextValue | undefined>(undefined)

export const SidePanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [payload, setPayload] = useState<SidePanelPayload | undefined>(undefined)
  const [closeCounter, setCloseCounter] = useState(0)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])

  useEffect(
    () =>
      subscribeSidePanelOpen((incomingPayload?: SidePanelPayload) => {
        setPayload(incomingPayload)
        setIsOpen(true)
      }),
    []
  )

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setPayload(undefined)
    setCloseCounter(prev => prev + 1)
  }, [])

  const value = useMemo(() => ({ open, close, isOpen, closeCounter }), [open, close, isOpen, closeCounter])

  return (
    <SidePanelContext.Provider value={value}>
      {children}
      <SidePanel open={isOpen} onClose={handleClose} payload={payload} />
    </SidePanelContext.Provider>
  )
}

export const useSidePanel = () => {
  const ctx = useContext(SidePanelContext)
  if (!ctx) {
    return {
      open: () => undefined,
      close: () => undefined,
      isOpen: false,
      closeCounter: 0
    }
  }

  return ctx
}

export default SidePanelContext
